import React, { useEffect, useState } from "react";
import { GraduationCap, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Target, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { DetailLevel } from "@/components/common/DetailLevel";
import { learningApi, getAiDetail, setAiDetail } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";

const SEV = {
    good: { icon: CheckCircle2, color: "hsl(152,52%,46%)" },
    watch: { icon: AlertTriangle, color: "hsl(38,92%,62%)" },
    focus: { icon: Target, color: "hsl(206,70%,52%)" },
};

export const CoachCard = ({ className = "" }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(getAiDetail());

    const load = async (refresh = false, dtl = detail) => {
        setLoading(true);
        try { setData(await learningApi.coach(refresh, dtl)); }
        catch (e) { setData(null); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(false); }, []);

    const changeDetail = (d) => { setDetail(d); setAiDetail(d); load(true, d); };

    return (
        <div className={`rounded-2xl border border-border bg-surface-1 p-5 ${className}`} data-testid="coach-card">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"><GraduationCap className="h-4 w-4" /></div>
                    <h3 className="font-sans-display text-sm font-semibold text-t1">{t("coach.title")}</h3>
                </div>
                <div className="flex items-center gap-1.5">
                    <DetailLevel value={detail} onChange={changeDetail} compact />
                    <Button data-testid="coach-refresh-button" variant="ghost" size="icon" className="h-8 w-8 text-t4 hover:text-t1" disabled={loading} onClick={() => load(true)}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    </Button>
                </div>
            </div>

            {loading && !data ? (
                <div className="space-y-3">
                    <Skeleton className="h-4 w-3/4 bg-surface-3" />
                    <Skeleton className="h-16 w-full rounded-xl bg-surface-2" />
                    <Skeleton className="h-16 w-full rounded-xl bg-surface-2" />
                </div>
            ) : data ? (
                <>
                    <p className="font-reading text-[15px] leading-relaxed text-t1">{data.headline}</p>
                    <div className="mt-4 space-y-2.5">
                        {(data.insights || []).map((ins, i) => {
                            const sev = SEV[ins.severity] || SEV.focus;
                            const Icon = sev.icon;
                            return (
                                <div key={i} className="rounded-xl bg-surface-2 p-3">
                                    <div className="flex items-center gap-2">
                                        <Icon className="h-4 w-4" style={{ color: sev.color }} />
                                        <span className="text-sm font-medium text-t1">{ins.title}</span>
                                    </div>
                                    {ins.detail && <p className="mt-1 text-sm text-t3">{ins.detail}</p>}
                                    {ins.action && <p className="mt-1.5 flex items-center gap-1 text-xs text-[hsl(var(--primary))]"><ArrowRight className="h-3 w-3" />{ins.action}</p>}
                                </div>
                            );
                        })}
                    </div>
                    {data.focus_topics?.length > 0 && (
                        <div className="mt-4">
                            <div className="mono-label mb-2 text-t4">{t("coach.focusTopics")}</div>
                            <div className="flex flex-wrap gap-1.5">
                                {data.focus_topics.map((tp, i) => (
                                    <span key={i} className="rounded-full border border-border bg-surface-2 px-2.5 py-1 text-xs text-t2">{tp}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {data.suggested_actions?.length > 0 && (
                        <div className="mt-4">
                            <div className="mono-label mb-2 text-t4">{t("coach.nextSteps")}</div>
                            <ul className="space-y-1.5">
                                {data.suggested_actions.map((a, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-t2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--primary))]" />{a}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            ) : (
                <p className="text-sm text-t3">{t("coach.empty")}</p>
            )}
        </div>
    );
};
